// ═══════════════════════════════════════════════════════════════════════
// Borne de pointage de site (mode C, conception § 4.2) — page kiosque, sans compte utilisateur.
//
// 1. Enrôlement : la RH enrôle la borne (gerer_bornes) et obtient une clé, montrée une seule fois.
//    Cette clé est saisie ici une fois ; elle reste dans le stockage local de la tablette.
// 2. Passage : le salarié choisit Arrivée, Départ, Début ou Fin de pause, puis présente le code
//    dynamique de son application (ou de sa page « Mon QR »). L'heure retenue est celle du SERVEUR.
// 3. Envoi : une clé d'idempotence par passage ; un renvoi après une coupure ne crée pas de doublon.
// Aucune photo n'est prise (décision D-18 non tranchée). Hors ligne : rien n'est gardé, la borne
// demande de signaler le passage au responsable (secours : scan ou appel nominal).
// ═══════════════════════════════════════════════════════════════════════
import { useCallback, useEffect, useRef, useState } from 'react';
import { LogIn, LogOut, Coffee, PlayCircle, CheckCircle, AlertTriangle, Settings, WifiOff } from 'lucide-react';
import ScannerQR from '../../components/ScannerQR';

const CLE_STOCKAGE = 'sogas_borne_cle';
const FORMAT_CLE = /^sgb1_[A-Za-z0-9_-]{43}$/;
const URL_PASSAGE = '/api/temps/borne/passages';
const DUREE_MESSAGE_MS = 6000;

const VERT = '#1B5E20';
const ROUGE = '#C62828';
const ORANGE = '#E65100';

const SENS = [
  { v: 'ARRIVEE', libelle: 'Arrivée', Icone: LogIn, couleur: VERT },
  { v: 'DEPART', libelle: 'Départ', Icone: LogOut, couleur: ROUGE },
  { v: 'DEBUT_PAUSE', libelle: 'Début de pause', Icone: Coffee, couleur: '#5D4037' },
  { v: 'FIN_PAUSE', libelle: 'Fin de pause', Icone: PlayCircle, couleur: '#1565C0' },
];

const lireCle = () => { try { return localStorage.getItem(CLE_STOCKAGE) || ''; } catch { return ''; } };
const ecrireCle = (v) => { try { if (v) localStorage.setItem(CLE_STOCKAGE, v); else localStorage.removeItem(CLE_STOCKAGE); } catch { /* stockage indisponible */ } };

function nouvelleCleIdempotence() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const octets = new Uint8Array(16);
  if (window.crypto && typeof window.crypto.getRandomValues === 'function') window.crypto.getRandomValues(octets);
  else for (let i = 0; i < octets.length; i += 1) octets[i] = Math.floor(Math.random() * 256);
  return Array.from(octets, (b) => b.toString(16).padStart(2, '0')).join('');
}

async function envoyerPassage(cle, corps) {
  const rep = await fetch(URL_PASSAGE, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'X-Cle-Borne': cle },
    body: JSON.stringify(corps),
    cache: 'no-store',
  });
  let donnees = null;
  try { donnees = await rep.json(); } catch { /* réponse vide */ }
  return { ok: rep.ok, statut: rep.status, donnees };
}

const pleinEcran = { minHeight: '100vh', background: '#F4F6F4', fontFamily: 'Segoe UI, Roboto, sans-serif', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', padding: 16, boxSizing: 'border-box' };

function Horloge() {
  const [maintenant, setMaintenant] = useState(new Date());
  useEffect(() => { const t = setInterval(() => setMaintenant(new Date()), 1000); return () => clearInterval(t); }, []);
  return (
    <div style={{ textAlign: 'center', marginBottom: 24 }}>
      <div style={{ fontSize: 56, fontWeight: 800, color: '#1a1a1a', lineHeight: 1 }}>
        {maintenant.toLocaleTimeString('fr-FR', { hour: '2-digit', minute: '2-digit', timeZone: 'Africa/Dakar' })}
      </div>
      <div style={{ fontSize: 16, color: '#666', marginTop: 6 }}>
        {maintenant.toLocaleDateString('fr-FR', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'Africa/Dakar' })}
      </div>
    </div>
  );
}

function Enrolement({ onEnregistrer }) {
  const [saisie, setSaisie] = useState('');
  const valide = FORMAT_CLE.test(saisie.trim());
  return (
    <div style={pleinEcran}>
      <div style={{ background: '#fff', borderRadius: 16, padding: 24, maxWidth: 520, width: '100%', boxShadow: '0 2px 12px rgba(0,0,0,0.08)' }}>
        <h1 style={{ fontSize: 22, margin: '0 0 8px', color: VERT, display: 'flex', alignItems: 'center', gap: 8 }}><Settings size={20} />Configurer la borne</h1>
        <p style={{ fontSize: 14, color: '#555', margin: '0 0 16px' }}>
          Saisissez la clé de borne remise par la RH lors de l'enrôlement. Elle n'est affichée qu'une fois :
          si elle est perdue, la RH révoque la borne et en enrôle une nouvelle.
        </p>
        <label htmlFor="cle-borne" style={{ fontSize: 13, fontWeight: 600, color: '#333' }}>Clé de la borne</label>
        <input id="cle-borne" type="password" autoComplete="off" value={saisie} onChange={(e) => setSaisie(e.target.value)}
          placeholder="sgb1_…" style={{ display: 'block', width: '100%', boxSizing: 'border-box', marginTop: 6, padding: '12px 14px', fontSize: 15, borderRadius: 10, border: '1px solid #ccc' }} />
        {saisie && !valide && <div style={{ color: ROUGE, fontSize: 13, marginTop: 6 }}>Format de clé non reconnu.</div>}
        <button type="button" disabled={!valide} onClick={() => onEnregistrer(saisie.trim())}
          style={{ marginTop: 16, width: '100%', padding: '14px 0', fontSize: 16, fontWeight: 700, borderRadius: 10, border: 'none', color: '#fff', background: valide ? VERT : '#bbb', cursor: valide ? 'pointer' : 'default' }}>
          Enregistrer la clé sur cette tablette
        </button>
      </div>
    </div>
  );
}

export default function BornePage() {
  const [cle, setCle] = useState(lireCle);
  const [sens, setSens] = useState(null);           // sens choisi, scanner ouvert
  const [envoi, setEnvoi] = useState(false);
  const [resultat, setResultat] = useState(null);   // { ok, titre, information, reseau }
  const minuterie = useRef(null);

  const afficher = useCallback((r) => {
    setResultat(r);
    if (minuterie.current) clearTimeout(minuterie.current);
    minuterie.current = setTimeout(() => setResultat(null), DUREE_MESSAGE_MS);
  }, []);
  useEffect(() => () => { if (minuterie.current) clearTimeout(minuterie.current); }, []);

  const apresScan = useCallback(async (payload) => {
    const sensChoisi = sens;
    setSens(null);
    setEnvoi(true);
    const corps = { payload, sens: sensChoisi, cle_idempotence: nouvelleCleIdempotence() };
    let rep = null;
    for (let essai = 0; essai < 2 && !rep; essai += 1) {
      try { rep = await envoyerPassage(cle, corps); } // même clé d'idempotence : un renvoi ne crée pas de doublon
      catch { if (essai === 0) await new Promise((ok) => setTimeout(ok, 1500)); }
    }
    setEnvoi(false);
    if (!rep) {
      afficher({ ok: false, reseau: true, titre: 'Réseau indisponible : signalez votre passage à votre responsable.' });
      return;
    }
    const d = rep.donnees || {};
    if (rep.ok && d.success) {
      afficher({ ok: true, titre: d.data.message, information: d.data.information });
      return;
    }
    if (d.code === 'BORNE_INCONNUE' || d.code === 'BORNE_REVOQUEE') {
      afficher({ ok: false, titre: d.message, borne: true });
      return;
    }
    afficher({ ok: false, titre: d.message || 'Passage non enregistré. Réessayez ou adressez-vous à votre responsable.' });
  }, [cle, sens, afficher]);

  if (!cle) return <Enrolement onEnregistrer={(v) => { ecrireCle(v); setCle(v); }} />;

  const oublier = () => {
    // eslint-disable-next-line no-alert
    if (window.confirm('Retirer la clé de cette borne ? Elle ne pourra plus enregistrer de passage.')) { ecrireCle(''); setCle(''); }
  };

  return (
    <div style={pleinEcran}>
      <Horloge />

      {envoi && <div role="status" style={{ fontSize: 20, color: '#555', marginBottom: 16 }}>Enregistrement…</div>}

      {resultat ? (
        <div role="alert" onClick={() => setResultat(null)}
          style={{ maxWidth: 640, width: '100%', borderRadius: 18, padding: 28, textAlign: 'center', cursor: 'pointer',
            background: resultat.ok ? '#E8F5E9' : '#FFEBEE', border: `3px solid ${resultat.ok ? '#A5D6A7' : '#FFCDD2'}` }}>
          {resultat.ok ? <CheckCircle size={56} color={VERT} /> : (resultat.reseau ? <WifiOff size={56} color={ROUGE} /> : <AlertTriangle size={56} color={ROUGE} />)}
          <div style={{ fontSize: 26, fontWeight: 800, marginTop: 12, color: resultat.ok ? VERT : ROUGE }}>{resultat.titre}</div>
          {resultat.information && (
            <div style={{ fontSize: 18, marginTop: 14, color: ORANGE, fontWeight: 600 }}>{resultat.information}</div>
          )}
          {resultat.borne && (
            <button type="button" onClick={oublier} style={{ marginTop: 16, background: 'none', border: '1px solid #999', borderRadius: 8, padding: '8px 14px', cursor: 'pointer' }}>
              Reconfigurer la borne
            </button>
          )}
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 16, maxWidth: 720, width: '100%' }}>
          {SENS.map(({ v, libelle, Icone, couleur }) => (
            <button key={v} type="button" disabled={envoi} onClick={() => setSens(v)}
              style={{ minHeight: 120, borderRadius: 18, border: 'none', background: couleur, color: '#fff', fontSize: 24, fontWeight: 800,
                display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 10, cursor: 'pointer', boxShadow: '0 3px 10px rgba(0,0,0,0.15)' }}>
              <Icone size={36} />{libelle}
            </button>
          ))}
        </div>
      )}

      <p style={{ fontSize: 14, color: '#777', marginTop: 24, textAlign: 'center' }}>
        Choisissez le sens de votre passage, puis présentez le code affiché par votre application SOGAS Employé.
      </p>
      <button type="button" onClick={oublier} style={{ marginTop: 8, background: 'none', border: 'none', color: '#aaa', fontSize: 12, cursor: 'pointer', display: 'flex', alignItems: 'center', gap: 4 }}>
        <Settings size={12} />Réglages de la borne
      </button>

      {sens && (
        <ScannerQR
          titre={`${SENS.find((s) => s.v === sens).libelle} : présentez votre code`}
          consigne="Présentez le code affiché par votre application SOGAS Employé"
          onScan={apresScan}
          onFermer={() => setSens(null)}
        />
      )}
    </div>
  );
}
